import type { Balance } from '@lms/shared-types'
import { toNumber } from './format'

/**
 * Negative accrual-stage annual balance display (feature-flagged via
 * `flags.negativeAccrualBalance`).
 *
 * Why this exists: an employee still inside their first-year monthly accrual
 * window can book more annual leave than they have accrued so far — the
 * canonical `available = accrued − used − pending` goes negative. Every
 * balance surface historically floored that figure at 0 (`Math.max(0, …)`,
 * `formatDays` clamping `n <= 0 → "0"`), which hid the deficit. These helpers
 * are the single source of truth for (a) detecting that accrual-stage-annual
 * condition and (b) rendering the signed value, so the employee home, manager,
 * and HR surfaces all decide identically.
 */

/** Deep-red text token for an overdrawn (negative) accrual-stage balance. */
export const NEGATIVE_BALANCE_COLOR = 'var(--danger-fg)'

/**
 * True when this balance is annual leave whose owner is still inside the
 * first-year monthly accrual window — i.e. the accrued-so-far cap is below the
 * full pro-rated yearly slice. Steady-state tenured employees have
 * `accruedEntitlement === entitlement`, so this is false for them.
 *
 * Falls back to `entitlement` when `accruedEntitlement` is absent (older mock
 * fixtures / payloads that don't carry it), which makes the two equal → not
 * accrual-stage. A surface that wants accrual-stage detection MUST send the
 * real `accruedEntitlement`.
 */
export function isAccrualStageAnnual(
  b: Pick<Balance, 'leaveType' | 'accruedEntitlement' | 'entitlement'>,
): boolean {
  if (b.leaveType !== 'annual') return false
  const accrued = b.accruedEntitlement ?? b.entitlement
  return toNumber(accrued) < toNumber(b.entitlement)
}

/**
 * Whether a surface should render this balance's real negative figure instead
 * of flooring it at 0. Only accrual-stage annual balances that have actually
 * been overdrawn (`available < 0`) qualify, and only when the feature flag is
 * on. Everything else keeps the historical 0-floor.
 */
export function shouldShowNegativeBalance(
  b: Pick<Balance, 'leaveType' | 'accruedEntitlement' | 'entitlement' | 'available'>,
  flagOn: boolean,
): boolean {
  return flagOn && isAccrualStageAnnual(b) && toNumber(b.available) < 0
}

/**
 * Day count for display, preserving a negative sign. Mirrors `formatDays`
 * (integers print plain, halves to one decimal) but does NOT floor negatives
 * to "0" — "-2", "-1.5" survive. Zero prints "0"; positives match `formatDays`.
 * Uses the ASCII hyphen-minus so it matches the existing HR roster's
 * `String(n)` rendering and stays test-friendly.
 */
export function formatSignedDays(value: unknown): string {
  const n = toNumber(value)
  if (n === 0) return '0'
  const abs = Math.abs(n)
  const body = Number.isInteger(abs) ? String(abs) : abs.toFixed(1).replace(/\.0$/, '')
  return n < 0 ? `-${body}` : body
}
